import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Dialog, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Archive,
  ChartLine,
  DollarBill,
  Pig,
  InProgressTask,
} from '@factorialco/f0-react/icons/app'
import {
  BudgetsDataCollectionDocument,
  FinanceValueObjectsBudgetableTypeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useArchiveBudget } from 'modules/finance/hooks/useBudgets'
import { amountWithCurrency } from 'modules/finance/lib/budgets'
import { events } from 'modules/finance/lib/events'

type BudgetStatus = 'within_budget' | 'budget_risk' | 'over_budget'
type BudgetStatusColor = 'viridian' | 'yellow' | 'radical'

interface BudgetStatusResult {
  status: BudgetStatus
  color: BudgetStatusColor
}

const getStatusLabel = (status: BudgetStatus): string => {
  const labels: Record<BudgetStatus, string> = {
    within_budget: i18n.t('budgets.list.card.status_within_budget'),
    budget_risk: i18n.t('budgets.list.card.status_budget_risk'),
    over_budget: i18n.t('budgets.list.card.status_over_budget'),
  }
  return labels[status]
}

const calculateBudgetStatus = (
  spentAmountCents: number | null | undefined,
  amountCents: number | null | undefined
): BudgetStatusResult => {
  const spent = Number(spentAmountCents || 0)
  const total = Number(amountCents || 0)
  const percentage = total > 0 ? (spent / total) * 100 : 0

  if (percentage > 100) {
    return { status: 'over_budget', color: 'radical' }
  } else if (percentage >= 80) {
    return { status: 'budget_risk', color: 'yellow' }
  }
  return { status: 'within_budget', color: 'viridian' }
}

const getBudgetTypeTag = (budgetableBy: string) => ({
  color: budgetableBy === 'expensable' ? 'viridian' : 'malibu',
  label:
    budgetableBy === 'expensable'
      ? i18n.t('budgets.detail.budget_type.expensable')
      : i18n.t('budgets.detail.budget_type.training_class'),
})

interface Props {
  canManageBudgets: boolean
  canCreateBudgets?: boolean
}

export const BudgetsDataCollection = ({ canManageBudgets, canCreateBudgets }: Props) => {
  const {
    employee: { id: employeeId },
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [budgetToArchive, setBudgetToArchive] = React.useState<number | null>(null)
  const [isArchiving, setIsArchiving] = React.useState(false)
  const { archiveBudget } = useArchiveBudget()

  const handleCreateBudget = React.useCallback(() => {
    track(events.clickAddBudget)
    navigate(navigation.expenses.budgets.list.new.fullPath())
  }, [navigate, navigation])

  const handleArchive = async () => {
    if (!budgetToArchive) return

    setIsArchiving(true)
    const result = await archiveBudget({ id: budgetToArchive })
    const handled = handleResult(result, () => undefined)
    setIsArchiving(false)

    if (handled.success) {
      setBudgetToArchive(null)
      app.setFlash({
        type: 'correct',
        message: i18n.t('budgets.forms.edit_budget.flash.budget_archive_ok'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('budgets.forms.edit_budget.flash.budget_archive_error'),
      })
    }
  }

  const source = useGraphqlDataCollectionSource({
    document: BudgetsDataCollectionDocument,
    mapPagination: (data) => {
      return {
        total: data.finance.budgetsConnection.totalCount ?? 0,
        cursor: data.finance.budgetsConnection.pageInfo.endCursor ?? undefined,
        hasNextPage: data.finance.budgetsConnection.pageInfo.hasNextPage ?? false,
      }
    },
    mapData: (data) => data.finance.budgetsConnection.edges.map((edge) => edge.node),
    search: {
      enabled: true,
    },
    presets: [
      {
        label: i18n.t('budgets.list.filters.active'),
        filter: { status: ['active'] },
      },
      {
        label: i18n.t('budgets.list.filters.archived'),
        filter: { status: ['archived'] },
      },
      {
        label: i18n.t('budgets.list.filters.budget_type.trainings'),
        filter: { budgetableBy: ['training_class'] },
      },
      {
        label: i18n.t('budgets.list.filters.budget_type.expenses'),
        filter: { budgetableBy: ['expensable'] },
      },
    ],
    filters: {
      status: {
        label: i18n.t('budgets.list.filters.status'),
        type: 'in',
        hidden: true,
        options: {
          options: [
            {
              label: i18n.t('budgets.list.tabs.active'),
              value: 'active',
            },
            {
              label: i18n.t('budgets.list.tabs.archive'),
              value: 'archived',
            },
          ],
        },
      },
      budgetableBy: {
        label: i18n.t('budgets.list.filters.budgetable_by'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('budgets.list.filters.budget_type.expenses'),
              value: 'expensable',
            },
            {
              label: i18n.t('budgets.list.filters.budget_type.trainings'),
              value: 'training_class',
            },
          ],
        },
      },
    },
    mapVariables: ({ search, filters }) => {
      const statusFilter: string[] = Array.isArray(filters?.status) ? filters.status : []
      const isArchived = statusFilter.includes('archived')
      const isActive = statusFilter.includes('active')
      const showAll = statusFilter.length === 0 || (isArchived && isActive)

      const budgetableByFilter: FinanceValueObjectsBudgetableTypeEnum[] | undefined = Array.isArray(
        filters?.budgetableBy
      )
        ? filters.budgetableBy.filter(
            (value): value is FinanceValueObjectsBudgetableTypeEnum =>
              value === 'expensable' || value === 'training_class'
          )
        : undefined

      return {
        includeInactive: showAll || isArchived,
        includeArchived: showAll || isArchived,
        onlyArchived: isArchived && !isActive,
        ownerOrViewerEmployeeId: canManageBudgets ? undefined : employeeId,
        search,
        budgetableBy: budgetableByFilter,
      }
    },
    primaryActions: canCreateBudgets
      ? () => ({
          label: i18n.t('budgets.navbar.add_budget'),
          icon: Add,
          onClick: handleCreateBudget,
        })
      : undefined,
    itemUrl: (item) => navigation.expenses.budgets.detail.fullPath({ budgetId: item.id }),
    itemActions: canManageBudgets
      ? (item) =>
          item.archived
            ? []
            : [
                {
                  label: i18n.t('budgets.list.actions.archive'),
                  icon: Archive,
                  onClick: () => {
                    setBudgetToArchive(item.id)
                  },
                },
              ]
      : undefined,
  })

  return (
    <>
      <OneDataCollection
        id="finance/budgets/v1"
        storage={{
          features: ['filters', 'sortings', 'search'],
        }}
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('budgets.list.table.name'),
                  id: 'name',
                  render: (item) => ({
                    type: 'text',
                    value: item.name,
                  }),
                },
                {
                  label: i18n.t('budgets.list.table.status'),
                  id: 'status',
                  render: (item) => {
                    const { status, color } = calculateBudgetStatus(
                      item.spentAmountCents,
                      item.amountCents
                    )

                    return {
                      type: 'dotTag',
                      value: {
                        color,
                        label: getStatusLabel(status),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('budgets.list.table.total_budget'),
                  id: 'totalBudget',
                  render: (item) => ({
                    type: 'text',
                    value: amountWithCurrency(
                      item.amountCents ? Number(item.amountCents) : 0,
                      item.currency,
                      locale
                    ),
                  }),
                },
                {
                  label: i18n.t('budgets.list.table.available_budget'),
                  id: 'availableBudget',
                  render: (item) => {
                    const available = Number(item.availableAmountCents || 0)
                    return {
                      type: 'text',
                      value: amountWithCurrency(available, item.currency, locale),
                    }
                  },
                },
                {
                  label: i18n.t('budgets.list.table.type'),
                  id: 'type',
                  render: (item) => ({
                    type: 'dotTag',
                    value: getBudgetTypeTag(item.budgetableBy),
                  }),
                },
              ],
            },
          },
          {
            type: 'card',
            options: {
              title: (item) => item.name,
              cardProperties: [
                {
                  label: i18n.t('budgets.list.card.status'),
                  icon: ChartLine,
                  render: (item) => {
                    const { status, color } = calculateBudgetStatus(
                      item.spentAmountCents,
                      item.amountCents
                    )

                    return {
                      type: 'dotTag',
                      value: {
                        color,
                        label: getStatusLabel(status),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('budgets.list.card.budget_type'),
                  icon: Pig,
                  render: (item) => ({
                    type: 'dotTag',
                    value: getBudgetTypeTag(item.budgetableBy),
                  }),
                },
                {
                  label: i18n.t('budgets.list.card.amount'),
                  icon: DollarBill,
                  render: (item) => ({
                    type: 'text',
                    value: amountWithCurrency(
                      item.amountCents ? Number(item.amountCents) : 0,
                      item.currency,
                      locale
                    ),
                  }),
                },
                {
                  label: i18n.t('budgets.list.card.progress'),
                  icon: InProgressTask,
                  render: (item) => {
                    const progressValue =
                      item.amountCents && item.amountCents > 0
                        ? (Number(item.spentAmountCents || 0) / Number(item.amountCents)) * 100
                        : 0
                    return {
                      type: 'progressBar',
                      value: {
                        value: Math.min(progressValue, 100),
                        max: 100,
                        label: `${Math.round(progressValue)}% ${i18n.t('budgets.list.card.allocated')}`,
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
      {budgetToArchive && (
        <Dialog
          onClose={() => setBudgetToArchive(null)}
          open={!!budgetToArchive}
          header={{
            title: i18n.t('budgets.detail.archive_modal.title'),
            description: i18n.t('budgets.detail.archive_modal.description'),
            type: 'warning',
          }}
          actions={{
            primary: {
              icon: Archive,
              label: i18n.t('common.archive'),
              onClick: handleArchive,
              disabled: isArchiving,
            },
            secondary: {
              label: i18n.t('common.cancel'),
              onClick: () => setBudgetToArchive(null),
              disabled: isArchiving,
            },
          }}
        />
      )}
    </>
  )
}
